import { Component, EventEmitter, Output, ChangeDetectionStrategy } from '@angular/core';
import { AdminHeader } from 'src/app/shared/models/admin-header.model';
import { NativeSoilService } from '../../worksheet/services/native-soil/native-soil.service';
import { ActivatedRoute } from '@angular/router';
import { SampleRow } from 'src/app/shared/models/SampleRow.model';
import { WorksheetService } from '../../worksheet/services/worksheet.service';
import { DateTimeUtils } from 'src/app/shared/util/date-time.utils';
export interface NativeSoilPivotRow {
  sampleId?: string;
  inHouseId: string;

  targetMc1?: string;
  targetWeight1?: number;
  actualWeight1?: string;
  tr1?: number;

  targetMc2?: string;
  targetWeight2?: number;
  actualWeight2?: string;
  tr2?: number;

  targetMc3?: string;
  targetWeight3?: number;
  actualWeight3?: string;
  tr3?: number;

  targetMc4?: string;
  targetWeight4?: number;
  actualWeight4?: string;
  tr4?: number;

  targetMc5?: string;
  targetWeight5?: number;
  actualWeight5?: string;
  tr5?: number;
}
@Component({
    selector: 'app-native-soil-print-table',
    templateUrl: './native-soil-print-table.component.html',
    styleUrls: ['./native-soil-print-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class NativeSoilPrintTableComponent {

  constructor(    
    private nativeSoilService: NativeSoilService,
    private worksheetService: WorksheetService,
    private route: ActivatedRoute){
  }

  private groupIdToIndex: { [key: string]: number } = {};

  asRecivedWorksheetsData: AdminHeader[] = [];
  moistureWorksheetsData: AdminHeader[] = [];
  nativeSoilWorksheetsData: AdminHeader[] = [];
  proctorWorksheetsData: AdminHeader[] = [];
  allWorksheetApiRes:any;
  @Output() allResNativeWorksheet = new EventEmitter<any>();

  receivedWorkSheet = [];
  moistureWorksheets = [];
  proctorWorksheets = [];
  workSheets = [];

  groupedRows: { groupId: string, rows: SampleRow[], showSeparator: boolean}[] = [];

  @Output() fetchAllHeader = new EventEmitter<{
    asRecivedWorksheets: AdminHeader[],
    moistureWorksheets: AdminHeader[],
    nativeSoilWorksheets: AdminHeader[],
    proctorWorksheets: AdminHeader[]
  }>();

  mcValues: (number | null)[] = [0, 0, 0, 0];
  dataSource: NativeSoilPivotRow[] = [];
  projectName: string = '';
  companyName: string = '';
  projectReference?: string;
  dueDate?: string;
  ovenDate?: string;
  isRush?: boolean;
  projectId: string = '';

  ngOnInit() {
    document.body.classList.add('print-no-header');

    this.route.queryParams.subscribe(params => {
      this.projectId = params['projectId'];

      this.getProject(this.projectId);
      this.getWorkSheets(this.projectId);
      this.getCompany(this.projectId);
    });
  }

  getDisplayedColumns(): string[] {
    const columns: string[] = ['sampleId', 'inHouseId'];
    columns.push('targetMc1', 'targetWeight1', 'actualWeight1', 'tr1');
    if (this.mcValues[0] !== null) { columns.push('targetMc2', 'targetWeight2', 'actualWeight2'); }
    if (this.mcValues[1] !== null) { columns.push('targetMc3', 'targetWeight3', 'actualWeight3'); }
    if (this.mcValues[2] !== null) { columns.push('targetMc4', 'targetWeight4', 'actualWeight4'); }
    if (this.mcValues[3] !== null) { columns.push('targetMc5', 'targetWeight5', 'actualWeight5'); }
    return columns;
  }

  ngOnDestroy(): void {
    document.body.classList.remove('print-no-header');
  }

  getWorkSheets(projectId: string): void {
    this.nativeSoilService.getNativeSoilWorkSheet(projectId).subscribe({
      next: (response: any) => {
        let initial = 1;
        this.mcValues = [ null, null, null, null ];
        this.moistureWorksheets = (response?.moistureWorksheets ?? []);
        this.workSheets = (response?.nativeSoilWorksheets ?? [])
          .filter((worksheet: any) => worksheet.parentRow);
        this.workSheets = (response?.nativeSoilWorksheets ?? [])
          .filter((worksheet: any) => worksheet.parentRow)
          .reduce((result: any[], worksheet: any) => {
            const existingIndex = result.findIndex(
              x => x.groupId === worksheet.groupId
            );

            if (existingIndex === -1) {
              // First worksheet for this group
              result.push(worksheet);
            } else {
              // Keep the worksheet with the highest labSpecMoistureContent
              const existing = result[existingIndex];

              if (
                Number(worksheet.labSpecMoistureContent ?? 0) >
                Number(existing.labSpecMoistureContent ?? 0)
              ) {
                result[existingIndex] = worksheet;
              }
            }
            return result;
          }, 
        []);

        // Same sorting order as Compaction
        this.workSheets.sort((a: any, b: any) => {
          // 1. Bulk worksheets always come before Tube worksheets
          if (a.worksheetType !== b.worksheetType) {
            if (a.worksheetType === 'Bulk') {
              return -1;
            }

            if (b.worksheetType === 'Bulk') {
              return 1;
            }
          }

          // BULK
          if (
            a.worksheetType === 'Bulk' &&
            b.worksheetType === 'Bulk'
          ) {
            // 2. Sample ID
            const sampleCompare = String(a.sampleId ?? '').localeCompare(
              String(b.sampleId ?? ''),
              undefined,
              {
                numeric: true,
                sensitivity: 'base'
              }
            );

            if (sampleCompare !== 0) {
              return sampleCompare;
            }

            // 3. Depth
            const depthCompare =
              Number(a.depth ?? 0) - Number(b.depth ?? 0);

            if (depthCompare !== 0) {
              return depthCompare;
            }

            // 4. Native Soil Sample ID
            const nativeSampleCompare =
              String(a.nativeSoilSampleId ?? '').localeCompare(
                String(b.nativeSoilSampleId ?? ''),
                undefined,
                {
                  numeric: true,
                  sensitivity: 'base'
                }
              );

            if (nativeSampleCompare !== 0) {
              return nativeSampleCompare;
            }

            // 5. Effort
            const effortCompare =
              Number(a.effort ?? 0) - Number(b.effort ?? 0);

            if (effortCompare !== 0) {
              return effortCompare;
            }

            // 6. Lab Spec Moisture Content DESC
            const moistureCompare =
              Number(b.labSpecMoistureContent ?? 0) -
              Number(a.labSpecMoistureContent ?? 0);

            if (moistureCompare !== 0) {
              return moistureCompare;
            }

            return 0;
          }

          // TUBE
          if (
            a.worksheetType === 'Tube' &&
            b.worksheetType === 'Tube'
          ) {
            // 2. Sample ID
            const sampleCompare = String(a.sampleId ?? '').localeCompare(
              String(b.sampleId ?? ''),
              undefined,
              {
                numeric: true,
                sensitivity: 'base'
              }
            );

            if (sampleCompare !== 0) {
              return sampleCompare;
            }

            // 3. Depth
            return Number(a.depth ?? 0) - Number(b.depth ?? 0);
          }

          return 0;
        });

        // Calculate initial/group order after sorting
        this.workSheets.forEach((worksheet: any) => {
          if (!worksheet.groupOrder) {
            initial += 1;
          }
        });
        this.buildTargetWeightsTable();
      },

      error: (err) => {
        console.error('Error fetching worksheets:', err);
      },
    });
  }

  buildTargetWeightsTable(): void {
    this.dataSource = [];

    this.workSheets.forEach((worksheet: any) => {
      if (worksheet.worksheetType === 'Bulk' && worksheet.labSpecMoistureContent) {
        const row: NativeSoilPivotRow = { sampleId: worksheet.sampleId, inHouseId: `[B]${worksheet.inHouseId ?? ''}` };
        const moistureWorksheet = (this.moistureWorksheets as any[]).find(
          (mw: any) =>
            mw.nativeSoilSampleId === worksheet.nativeSoilSampleId &&
            mw.compactionEffort === worksheet.effort
        );
        const highestMoistureContent = moistureWorksheet?.multipleMoistureResults
          ?.map((x: any) => Number(x.moistureContent))
          .filter((value: number) => !isNaN(value))
          .sort((a: number, b: number) => b - a)[0] ?? null;
        row.targetMc1 = highestMoistureContent != null ? highestMoistureContent.toFixed(1) : '';
        row.targetWeight1 = this.calculateTargetWeight(worksheet, highestMoistureContent, worksheet.labSpecMoistureContent);
        row.actualWeight1 = this.getActualWeight(this.getTestResult(worksheet, 1)?.weight, row.targetWeight1);
        row.tr1 = this.getTestResult(worksheet, 1)?.tr ?? '';

        if (this.mcValues[0] !== null) {
          row.targetMc2 = this.mcValues[0].toFixed(1);
          row.targetWeight2 = this.calculateTargetWeight(worksheet, this.mcValues[0], worksheet.labSpecMoistureContent);
          row.actualWeight2 = '';
        }

        if (this.mcValues[1] !== null) {
          row.targetMc3 = this.mcValues[1].toFixed(1);
          row.targetWeight3 = this.calculateTargetWeight(worksheet, this.mcValues[1], worksheet.labSpecMoistureContent);
          row.actualWeight3 = '';
        }

        if (this.mcValues[2] !== null) {
          row.targetMc4 = this.mcValues[2].toFixed(1);
          row.targetWeight4 = this.calculateTargetWeight(worksheet, this.mcValues[2], worksheet.labSpecMoistureContent);
          row.actualWeight4 = '';
        }

        if (this.mcValues[3] !== null) {
          row.targetMc5 = this.mcValues[3].toFixed(1);
          row.targetWeight5 = this.calculateTargetWeight(worksheet, this.mcValues[3], worksheet.labSpecMoistureContent);
          row.actualWeight5 = '';
        }

        this.dataSource.push(this.blankZeroValues(row));
      } else {
        this.dataSource.push({
          sampleId: worksheet.sampleId,
          inHouseId: `[T]${worksheet.inHouseId ?? ''}`
        });
      }
    });
  }

  // Show an empty cell instead of 0 for the weight and TR columns (moisture values keep their 0)
  blankZeroValues(row: NativeSoilPivotRow): NativeSoilPivotRow {
    const result: any = { ...row };
    Object.keys(result).forEach((key) => {
      if (key === 'sampleId' || key === 'inHouseId' || key.startsWith('targetMc')) { return; }
      const value = result[key];
      if (value !== '' && value !== null && value !== undefined && Number(value) === 0) {
        result[key] = '';
      }
    });
    return result;
  }

  getTestResult(worksheet: any, testNumber: number): any {
    const result = worksheet.nativeSoilWorkSheetTestResults?.find(
      (test: any) => test.testNumber === testNumber
    );
    if (!result) { return null; }
    return {...result, weight: result.weight === 0 ? '' : result.weight, tr: result.tr === 0 ? '' : result.tr };
  }

  getActualWeight(weight: number | null | undefined, targetWeight: number | null | undefined): string {
    if (weight == null) { return ''; }

    if (targetWeight != null && (weight < targetWeight - 7 || weight > targetWeight + 7)) {
      return `${weight} *`;
    }
    return `${weight}`;
  }

  calculateTargetWeight(worksheet: any, moisture: number, labSpecifiedMoisture: number): number {
    var drySoilWeight = (worksheet.weightPerLayer * 5) / (1 + labSpecifiedMoisture / 100);
    if (moisture == 0) { 
      return Math.round(Number(drySoilWeight) + Number(worksheet.mouldPlusProbeTare)); 
    }

    var targetWeight = drySoilWeight * (1 + moisture / 100) +  worksheet.mouldPlusProbeTare;
    return Math.round(targetWeight);
  }

  setDepth(elem: any): string {
    let depthRange = '';
    if (elem.depth !== null) {
      depthRange = `${elem.depth}`;
    }
    if (elem.endDepth) {
      if (elem.depth !== null && elem.endDepth === null) {
        depthRange = `${elem.depth}`;
      }
      if (elem.endDepth !== null && elem.depth !== null) {
        depthRange = `${elem.depth}-${elem.endDepth}`;
      }
    }
    return depthRange;
  }

  printTable() {
    window.print();
  }

  getRowColor(groupId: string): string {
    const index = this.groupIdToIndex[groupId];
    return index % 2 === 0 ? 'white-row' : 'gray-row';
  }

  getProject(projectId : string)
  {
    const projectResponse = this.worksheetService.fetchProjectById(projectId);
    projectResponse.subscribe(res=>{
      this.projectName = res.projectName;
      this.projectReference = res.referenceId;
      this.dueDate = res.dueDate ? DateTimeUtils.getLocalDateTimeFromUTCDate(res.dueDate, 'MM/dd/yyyy').toString() : '';
      this.ovenDate = res.ovenDate;
      this.isRush = res.rushTesting;
    });
  }

  getCompany(companyId: string)
  {
    const companyResponse = this.worksheetService.fetchCompanyById(companyId);
    companyResponse.subscribe((res: any)=>{
      this.companyName = res.company.name;
    })
  }

  shouldShowSeparator(group: any): boolean {
    const prevGroup = this.groupedRows[this.groupedRows.indexOf(group) - 1];
    const currentGroup = this.groupedRows[this.groupedRows.indexOf(group)];

    if (!prevGroup?.rows?.length || !currentGroup?.rows?.length) {
      return false;
    }
    var result = prevGroup.rows[0].nativeSoilSampleId !== currentGroup.rows[0].nativeSoilSampleId;
    return result;
  }
}
